import React, { useMemo } from "react";
import { MoneyDirection } from "@common/constants";
import { roundToCents } from "@common/helpers/monetary";
import { MatchableItem, MatchItemType } from "./types";
import { DocumentMatchFooter } from "./DocumentMatchFooter";
import { computeDocumentMatchDiff } from "./documentMatchDiff";
import { useMatchesPanelExtraHeightVariable } from "./useMatchesPanelResize";
import { SearchMatchesSection } from "./SearchMatchesSection";
import { AccrualMatchSearchMode } from "./matchDrawerDefaults";
import { useAccrualMatch } from "./useAccrualMatch";
import { MatchScheduleColumnProvider } from "./schedule/matchScheduleColumn.context";
import styles from "./AccrualMatchPage.module.scss";

interface Props {
    documentType: "bill" | "invoice";
    documentId: string;
    sourceItem: MatchableItem;
    originalItems: MatchableItem[];
    onEditAllAllocations: (items: MatchableItem[]) => void;
}

const TRANSACTION_ONLY_TYPE_OPTIONS = [
    { value: MatchItemType.TRANSACTION, label: "Transaction" },
];

export const DocumentMatchPage: React.FC<Props> = ({
    documentType,
    documentId,
    sourceItem,
    originalItems,
    onEditAllAllocations,
}) => {
    const { selectedItems } = useAccrualMatch();

    const documentAnchoredSearch = useMemo(
        () => ({
            transactionDirection:
                documentType === "invoice"
                    ? MoneyDirection.MONEY_IN
                    : MoneyDirection.MONEY_OUT,
            suggestedForInvoiceId:
                documentType === "invoice" ? documentId : undefined,
        }),
        [documentType, documentId],
    );

    const baseOpenAmount = sourceItem.openAmount ?? Math.abs(sourceItem.amount);

    const diff = useMemo(
        () => computeDocumentMatchDiff(selectedItems, originalItems),
        [selectedItems, originalItems],
    );

    const projectedSourceItem = useMemo<MatchableItem>(
        () => ({
            ...sourceItem,
            openAmount: roundToCents(
                baseOpenAmount + diff.removedAmount - diff.addAmount,
            ),
        }),
        [sourceItem, baseOpenAmount, diff],
    );

    const { sectionRef, setMatchesPanelExtraHeight } =
        useMatchesPanelExtraHeightVariable();

    return (
        <MatchScheduleColumnProvider
            sourceItem={projectedSourceItem}
            selectedItems={selectedItems}
        >
            <div className={styles.page}>
                <div className={styles.subtitleBar}>
                    <span>Step 1 of 2</span>
                    <span>
                        Find and confirm matches or resolve the difference
                    </span>
                </div>

                <div className={styles.searchSection} ref={sectionRef}>
                    <SearchMatchesSection
                        defaultType={MatchItemType.TRANSACTION}
                        defaultSearchMode={
                            documentType === "invoice"
                                ? AccrualMatchSearchMode.SUGGESTED
                                : AccrualMatchSearchMode.ALL
                        }
                        searchMatchTypeOptions={TRANSACTION_ONLY_TYPE_OPTIONS}
                        documentAnchoredSearch={documentAnchoredSearch}
                        renderFooter={(searchFooterState) => (
                            <DocumentMatchFooter
                                documentType={documentType}
                                documentId={documentId}
                                sourceItem={projectedSourceItem}
                                sourceOpenAmount={baseOpenAmount}
                                originalItems={originalItems}
                                diff={diff}
                                searchFooterState={searchFooterState}
                                onEditAllAllocations={onEditAllAllocations}
                                onMatchesPanelExtraHeightChange={
                                    setMatchesPanelExtraHeight
                                }
                            />
                        )}
                    />
                </div>
            </div>
        </MatchScheduleColumnProvider>
    );
};
